<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { app } from 'src/stores/session/native-bridge';
	//Helpers
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { real_id } from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { executeModeReplacement } from '../../../../helpers/mode_switch_helpers';
	import {
		checkSharedChannels,
		unshareAllChannels,
		type SharedChannelData,
	} from '../../../../helpers/profile_change_shared_helper';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Loader, Select } from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	//Components
	import ProfileChangeSharedModal from './ProfileChangeSharedModal.svelte';
	import { drawerRef } from 'src/stores/ui/sidepanel-public';

	export let device;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;

	let options: string[];
	let i18nOptions: string[];
	let value: string | SelectOptionPropsType;
	let cache: string;
	let loading: boolean = false;
	let showModal: boolean = false;
	let sharedChannelsData: SharedChannelData[] = [];

	const selectMode: (e: CustomEvent<SelectOptionPropsType>) => void = (e) => {
		value = e.detail;
	};

	const handleSave: () => Promise<void> = async () => {
		if (cache === value) return;

		const channelsData = await checkSharedChannels(device);

		if (channelsData.length > 0) {
			sharedChannelsData = channelsData;
			showModal = true;
			return;
		}

		await sendReq();
	};

	const sendReq: () => Promise<void> = async () => {
		if (cache === value) return;
		loading = true;
		showModal = false;

		await unshareAllChannels(sharedChannelsData);

		const valueStr = typeof value === 'string' ? value : String(value);
		const params = {
			id: device.channel,
			name: options[i18nOptions.indexOf(valueStr)],
		};

		const res: SettingRequest = await gen2Request(device, 'Shelly.SetProfile', params);

		const response = onSettingsResponse(res, device, undefined, true);

		if (response?.isok) {
			const _device = $devices[id];
			const mode = options[i18nOptions.indexOf(valueStr)];
			settings.sys.device.profile = mode;

			if ($app) {
				$drawerRef.close();
			} else {
				resetDrawer();
			}

			await executeModeReplacement(_device, mode, 'mode', true);
			cache = valueStr;
		} else {
			value = cache;
		}
		loading = false;
	};

	const getData: () => Promise<string[]> = async () => {
		const getProfiles = await gen2Request(device, 'Shelly.ListProfiles', {});

		options =
			typeof (getProfiles as any)?.data !== 'undefined'
				? Object.keys((getProfiles as any).data.profiles)
				: typeof (getProfiles as any)?.result !== 'undefined'
					? Object.keys((getProfiles as any).result.profiles)
					: ['light', 'rgbcct', 'cctx2', 'rgbx2light'];
		i18nOptions = [
			$format(`option_${options[0]}`, { default: options[0] }),
			$format(`option_${options[1]}`, { default: options[1] }),
			$format(`option_${options[2]}`, { default: options[2] }),
			$format(`option_${options[3]}`, { default: options[3] }),
		];
		value = i18nOptions[options.indexOf(settings?.sys?.device?.profile)];
		cache = typeof value === 'string' ? value : String(value);
		return i18nOptions;
	};
</script>

{#await getData()}
	<Loader />
{:then _}
	<Select
		class="mb-s"
		size="m"
		{value}
		options={i18nOptions}
		icon={'fas fa-info-circle'}
		info={$format('device_profile_desc', {
			default:
				'Warning: Changing device profile would restart the device and clear all existing schedules and actions.',
		})}
		on:change={selectMode}
		aria-label={$format('device_profile', { default: 'Device profile' })}
		id="select-o5bm4v4"
	/>
{/await}

{#if sharedChannelsData.length > 0}
	<ProfileChangeSharedModal bind:showModal on:confirm={sendReq} />
{/if}

<Button class="mt-s" {loading} size="m" width="full" on:click={handleSave}
	>{$format('button_save')}</Button
>
